/**
 * Canonical mapping of backend Specialty Service slugs → public-facing
 * display labels + brand descriptors. Any UI showing a service to a user
 * (Contractor Dashboard, PM Command Center, Ops drilldowns, Admin tables)
 * should pull labels from here so a rename never has to touch the DB.
 *
 * NEVER rename the slug keys — they are the DB source of truth
 * (property.service_policies.<slug>). Only edit the display label.
 */
export const SPECIALTY_LABELS = {
  bulk_removal: 'Bulk Removal',
  pressure_washing: 'Pressure Washing',
  window_cleaning: 'Window Cleaning',
  dumpster_treatment: 'Dumpster Treatment',
  trash_room_maintenance: 'Trash Room Maintenance',
  eco_friendly_amenities: 'Eco-Friendly Amenities',
  // Iter-176 · Phase 2 — full catalog. Deep Cleaning + Floor Care +
  // Day Porter now first-class specialty options everywhere they
  // render.
  deep_cleaning: 'Deep Cleaning',
  janitorial: 'Janitorial Services',
  floor_care: 'Floor Care',
  floor_polishing: 'Floor Polishing',
  floor_restoration: 'Floor Restoration',
  day_porter: 'Day Porter',
};

export const SPECIALTY_DESCRIPTIONS = {
  bulk_removal:
    'Large-item pickup — furniture, mattresses, and other bulk household waste placed at the designated pickup zone.',
  pressure_washing:
    'Exterior pressure washing of walkways, breezeways, stairwells, and dumpster pads.',
  window_cleaning:
    'Interior + exterior window cleaning of leasing offices and designated common areas.',
  dumpster_treatment:
    'Deodorizing, sanitizing, and pest-deterrent treatment of dumpster interiors and pads.',
  trash_room_maintenance:
    'Deep clean of interior trash rooms + odor control between service days.',
  eco_friendly_amenities:
    'Eco-friendly amenity cleaning — clubhouse, fitness center, leasing office, using green-certified products.',
  deep_cleaning:
    'Full-scope deep cleaning for common areas, amenity spaces, or move-out units — restores surfaces beyond routine cleaning cadence.',
  janitorial:
    'Recurring janitorial cleaning for offices, lobbies, restrooms, and back-of-house — tailored to your facility cadence.',
  floor_care:
    'Routine floor cleaning programs matched to every surface type — lobbies, corridors, service areas.',
  floor_polishing:
    'Professional polishing that restores shine, protects surfaces and keeps floors presentation-ready.',
  floor_restoration:
    'Deep restoration programs that bring worn floors back to a professional standard and extend their life.',
  day_porter:
    'On-site NECS porter during business hours — spot-clean, restock, quick-response support.',
};

/** Turn a slug into its label, falling back to a title-cased version of the
 *  raw slug for any new service the frontend hasn't been taught yet. */
export const specialtyLabel = (slug) => {
  if (!slug) return '';
  if (SPECIALTY_LABELS[slug]) return SPECIALTY_LABELS[slug];
  return String(slug)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Given a property object, return the list of active specialty services as
 *  `{ slug, label, description, policy }` objects — sorted alphabetically
 *  by label so the display order is deterministic across dashboards. */
export const activeSpecialtyServices = (property) => {
  const pol = property?.service_policies || {};
  return Object.keys(pol)
    .filter((k) => pol[k] && (pol[k].enabled === undefined || pol[k].enabled))
    .map((slug) => ({
      slug,
      label: specialtyLabel(slug),
      description: SPECIALTY_DESCRIPTIONS[slug] || '',
      policy: pol[slug] || {},
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** Human-friendly frequency label. */
export const frequencyLabel = (freq) => {
  if (!freq) return '';
  const map = {
    weekly: 'Weekly',
    biweekly: 'Bi-weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    biannual: 'Bi-annually',
    annual: 'Annually',
    on_demand: 'On demand',
  };
  return map[freq] || String(freq).replace(/_/g, ' ');
};

/**
 * Legacy tier-name aliases — display-side only. Iter-70 renamed the 6 tiers
 * to Collection names, but a lot of pre-existing property docs in MongoDB
 * still carry the old strings ("Premium Package", "Platinum Package", etc.).
 * Rather than run a destructive Mongo migration, every UI that shows a
 * property tier pipes it through `canonicalCollection()` so the badge
 * renders the canonical name without touching the DB. Case-insensitive.
 *
 * IMPORTANT: keep this map in sync with `_LEGACY_COLLECTION_ALIASES`
 * in /app/backend/routes/leads.py (backend uses it for the demo seeder).
 */
const LEGACY_COLLECTION_ALIASES = {
  'standard package': 'Essential Collection',
  'essential': 'Essential Collection',
  'premium package': 'Professional Collection',
  'professional': 'Professional Collection',
  'platinum package': 'Elite Collection',
  'elite': 'Elite Collection',
  'magnum package': 'Signature Collection',
  'signature': 'Signature Collection',
  'noble package': 'Noble Empire Executive Collection',
  'noble empire package': 'Noble Empire Executive Collection',
  'executive': 'Noble Empire Executive Collection',
  'executive collection': 'Noble Empire Executive Collection',
  'deluxe noble package': 'Noble Empire Presidential Collection',
  'deluxe empire package': 'Noble Empire Presidential Collection',
  'presidential': 'Noble Empire Presidential Collection',
  'presidential collection': 'Noble Empire Presidential Collection',
};

const CANONICAL_COLLECTIONS = new Set([
  'Essential Collection',
  'Professional Collection',
  'Elite Collection',
  'Signature Collection',
  'Noble Empire Executive Collection',
  'Noble Empire Presidential Collection',
]);

export const canonicalCollection = (tier) => {
  if (!tier) return '';
  const raw = String(tier).trim();
  if (CANONICAL_COLLECTIONS.has(raw)) return raw;
  const key = raw.toLowerCase();
  if (LEGACY_COLLECTION_ALIASES[key]) return LEGACY_COLLECTION_ALIASES[key];
  // Titlecase fallback so unknown tiers still look presentable.
  return raw.replace(/\b\w/g, (c) => c.toUpperCase());
};
